import React, { useState, useEffect } from 'react';
import {
  Box, Chip, CircularProgress, Alert, Typography,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Axios } from 'services/axios';

const ACCENT = '#667eea';

// Selectable add-ons grouped by subcategory — sourced from the shop
// "Additional Features" (/shop/additional-features). The selected ids are
// AdditionalFeature ids and are stored on the mapping as meta.addOnIds.
// Self-contained: fetches on mount. Controlled via selectedIds + onToggle.
// `categoryIds` (from the questionnaire) restricts which add-on subcategories
// are shown — each subcategory belongs to one main category (sc.categoryId).
function AddOnsSelector({ selectedIds = [], onToggle, categoryIds }) {
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await Axios.fetch('/shop/additional-features');
        const list = res.data?.data || res.data;
        if (active) setSubCategories(Array.isArray(list) ? list : []);
      } catch {
        if (active) setSubCategories([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Restrict to the questionnaire's selected categories. When none are
  // configured, show all (backward compatible).
  const allowed = Array.isArray(categoryIds) ? categoryIds.map(String) : [];
  const scopedSubCategories = allowed.length
    ? subCategories.filter(sc => allowed.includes(String(sc.categoryId)))
    : subCategories;

  // Only subcategories that actually have features are worth showing.
  const groups = scopedSubCategories
    .map(sc => ({
      id: sc.id,
      name: sc.subCategoryName || 'Uncategorized',
      features: Array.isArray(sc.features) ? sc.features : [],
    }))
    .filter(g => g.features.length > 0);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1.5} sx={{ p: 3, border: '1px solid #e5e7eb', borderRadius: 2 }}>
        <CircularProgress size={18} sx={{ color: ACCENT }} />
        <Typography sx={{ fontSize: 13, color: '#6b7280' }}>Loading additional features…</Typography>
      </Box>
    );
  }

  if (groups.length === 0) {
    return <Alert severity="info">No additional features available. Create them under Categories → Features first.</Alert>;
  }

  return (
    <Box>
      {groups.map((group) => {
        const selectedInCat = group.features.filter(f => selectedIds.includes(f.id)).length;
        return (
          <Accordion key={group.id} defaultExpanded disableGutters
            sx={{ border: '1px solid #e5e7eb', borderRadius: 2, mb: 1, boxShadow: 'none', '&:before': { display: 'none' }, overflow: 'hidden' }}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: '#fafafa', minHeight: 44, '& .MuiAccordionSummary-content': { my: 1 } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                {group.name} ({group.features.length})
                {selectedInCat > 0 && (
                  <Chip label={`${selectedInCat} selected`} size="small"
                    sx={{ ml: 1, height: 18, fontSize: 10, fontWeight: 700, bgcolor: `${ACCENT}18`, color: ACCENT }} />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1.2 }}>
              {group.features.map(feature => {
                const isSelected = selectedIds.includes(feature.id);
                return (
                  <Box key={feature.id} onClick={() => onToggle(feature.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.2, p: 1.2, mb: 0.8, borderRadius: 1.5, cursor: 'pointer',
                      border: '1px solid', borderColor: isSelected ? 'rgba(102,126,234,0.45)' : '#e5e7eb',
                      bgcolor: isSelected ? 'rgba(102,126,234,0.06)' : '#fff',
                      '&:last-child': { mb: 0 }, '&:hover': { bgcolor: 'rgba(102,126,234,0.04)' }, transition: 'all 0.15s',
                    }}>
                    <Box sx={{ width: 18, height: 18, flexShrink: 0, borderRadius: '4px', border: `2px solid ${isSelected ? ACCENT : '#d1d5db'}`, bgcolor: isSelected ? ACCENT : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isSelected && <span style={{ color: '#fff', fontSize: 11, fontWeight: 900 }}>✓</span>}
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography sx={{ fontSize: 13, fontWeight: isSelected ? 600 : 500, color: '#111827', lineHeight: 1.3 }}>
                        {feature.name}
                      </Typography>
                      {feature.description && (
                        <Typography sx={{ fontSize: 11, color: '#6b7280', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {feature.description}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.7} flexShrink={0}>
                      {(feature.featureDefaultPrice !== undefined && feature.featureDefaultPrice !== null && feature.featureDefaultPrice !== '') && (
                        <Chip label={`$${feature.featureDefaultPrice}`} size="small"
                          sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: 'rgba(22,163,74,0.12)', color: '#16a34a' }} />
                      )}
                      <Chip label={feature.active ? 'Active' : 'Inactive'} size="small" variant="outlined"
                        sx={{ height: 22, fontSize: 10, fontWeight: 700, color: feature.active ? '#16a34a' : '#6b7280', borderColor: feature.active ? 'rgba(22,163,74,0.4)' : 'rgba(107,114,128,0.3)' }} />
                    </Box>
                  </Box>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

export default AddOnsSelector;
